import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';
import { getApiErrorMessage, isRecordNotFound } from '../api-error';
import type { ApiClientOptions, ApiRequestConfig } from '../type';
import { getAuthTokenProvider } from './auth-bridge';

/**
 * The workspace's single Axios instance.
 *
 * The legacy app forked this four ways - `http`, `http.idtoken`,
 * `http.notification`, `http.withouterror` - which meant four copies of the auth
 * header logic drifting apart. Here there is one instance and the differences are
 * per-request options (`useIdToken`, `silent`) on `ApiRequestConfig`.
 */
let instance: AxiosInstance | null = null;

const requireInstance = (): AxiosInstance => {
  if (!instance) {
    throw new Error(
      'API client used before createApiClient() ran. Each app must call it in main.tsx ' +
        'before rendering.'
    );
  }
  return instance;
};

/**
 * Reports a business failure once, as a toast.
 *
 * "Record Not Found" is deliberately swallowed: the backend answers with it for an
 * empty list on plenty of endpoints, and surfacing that as an error trains users
 * to ignore error toasts entirely.
 */
const reportFailure = (message: string | undefined) => {
  if (isRecordNotFound(message)) return;
  toast.error(message?.trim() || 'Something went wrong. Please try again.');
};

/**
 * Query-string serialiser: an object-valued param travels as **JSON**.
 *
 * The backend reads list filters from a single `data` param holding a JSON document —
 * `?data={"list_filter":{"limit":10,"offset":0}}`. axios's default flattens a nested object into
 * brackets instead (`data[list_filter][limit]=10`), which the endpoint cannot parse and answers
 * with `ERROR | TypeError | An unexpected error occurred.` Arrays keep axios's `key[]=` form;
 * scalars are unchanged.
 */
export const serializeParams = (params: Record<string, unknown> | undefined): string => {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === undefined || value === null) continue;

    if (Array.isArray(value)) {
      value.forEach((item) => search.append(`${key}[]`, String(item)));
    } else if (value instanceof Date) {
      search.append(key, value.toISOString());
    } else if (typeof value === 'object') {
      search.append(key, JSON.stringify(value));
    } else {
      search.append(key, String(value));
    }
  }

  return search.toString();
};

export const createApiClient = ({
  baseURL,
  apiKey,
  applicationId,
  appUrl,
}: ApiClientOptions): AxiosInstance => {
  const client = axios.create({ baseURL, paramsSerializer: { serialize: serializeParams } });

  client.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const request = config as InternalAxiosRequestConfig & ApiRequestConfig;
    const provider = getAuthTokenProvider();

    if (provider) {
      const token = request.useIdToken
        ? await provider.getIdToken()
        : await provider.getAccessToken();

      if (token) {
        config.headers.set('Authorization', token);
      } else {
        // No usable session. Let the app tear down rather than firing a request
        // that is guaranteed to 401 and then reporting it as a server error.
        provider.onSessionExpired();
      }
    }

    if (apiKey) config.headers.set('x-api-key', apiKey);

    /**
     * Identifies the calling portal to the backend.
     *
     * `app_url` is a REGISTERED application URL, not the portal's own address and
     * not `REACT_APP_REDIRECT_URL` - the backend matches it against a registry,
     * and an unrecognised value is rejected. Each app passes its own constant
     * (see `APP_URL` in its `main.tsx`); the legacy clients hardcoded
     * `https://sdmls.mlsrouter.realtyfeed.com` for mls and
     * `https://vendor.mlsrouter.realtyfeed.com` for vendor.
     *
     * The usage-report service is excluded, exactly as the legacy client
     * excluded it - it rejects the pair rather than ignoring it.
     */
    const isUsageReport = config.url?.includes('/usage-report/') ?? false;
    if (!isUsageReport) {
      if (applicationId !== undefined) config.headers.set('app_id', String(applicationId));
      if (appUrl) config.headers.set('app_url', appUrl);
    }

    return config;
  });

  client.interceptors.response.use(
    (response) => {
      const request = response.config as ApiRequestConfig;
      const body = response.data as { is_success?: boolean; success?: boolean } | undefined;

      /**
       * A business failure arrives as HTTP 200 with `is_success: false`, so the
       * status alone never tells you whether a call worked. Reporting it here
       * means every caller gets the toast without repeating the check - callers
       * that render the failure themselves pass `silent: true`.
       *
       * `success: true` is checked as well because a few endpoints answer with
       * that field instead and would otherwise be reported as failures.
       */
      if (body && body.is_success === false && body.success !== true && !request.silent) {
        reportFailure(getApiErrorMessage(body));
      }

      return response;
    },
    (error) => {
      const request = (error.config ?? {}) as ApiRequestConfig;
      if (!request.silent) toast.error(getApiErrorMessage(error));
      return Promise.reject(error);
    }
  );

  instance = client;
  return client;
};

/**
 * The configured client. Data-access hooks import this rather than the instance,
 * so a test can swap the client without every module having captured it at import
 * time.
 */
export const apiClient = {
  get: <T>(url: string, config?: ApiRequestConfig) => requireInstance().get<T>(url, config),
  post: <T>(url: string, data?: unknown, config?: ApiRequestConfig) =>
    requireInstance().post<T>(url, data, config),
  put: <T>(url: string, data?: unknown, config?: ApiRequestConfig) =>
    requireInstance().put<T>(url, data, config),
  patch: <T>(url: string, data?: unknown, config?: ApiRequestConfig) =>
    requireInstance().patch<T>(url, data, config),
  delete: <T>(url: string, config?: ApiRequestConfig) => requireInstance().delete<T>(url, config),
};

/** Test seam - drops the instance so suites don't leak one into the next. */
export const resetApiClient = (): void => {
  instance = null;
};
